/** @jsxImportSource @emotion/react */
/**
 * Компонент для загрузки и отображения фотографий посадки
 * Поддерживает offline-режим с локальным хранилищем
 */

import React, { useState } from 'react';
import { css } from '@emotion/react';
import commonStyles from '../../styles/commonStyles';
import {
  Upload,
  Button,
  Card,
  Image,
  Space,
  Tag,
  Tooltip,
  Modal,
  Input,
  Badge,
} from 'antd';
import {
  CameraOutlined,
  DeleteOutlined,
  CloudUploadOutlined,
  CloudOutlined,
  ExclamationCircleOutlined,
  CheckCircleOutlined,
  SyncOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';
import { usePlantingPhotos } from '../../hooks/usePlantingPhotos';
import type { PlantingPhoto } from '../../models/Planting';
import type { PhotoSyncStatus } from '../../utils/imageStorage';

const { TextArea } = Input;

interface PlantingPhotosProps {
  saveId: string;
  plantingId: string | number;
  remotePhotos?: PlantingPhoto[];
  readonly?: boolean;
}

const getSyncStatusIcon = (status: PhotoSyncStatus) => {
  switch (status) {
    case 'pending':
      return <ClockCircleOutlined css={css({ color: '#faad14' })} />;
    case 'uploading':
      return <SyncOutlined spin css={css({ color: '#1890ff' })} />;
    case 'synced':
      return <CheckCircleOutlined css={css({ color: '#52c41a' })} />;
    case 'failed':
      return <ExclamationCircleOutlined css={css({ color: '#ff4d4f' })} />;
  }
};

const getSyncStatusText = (status: PhotoSyncStatus) => {
  switch (status) {
    case 'pending':
    case 'uploading':
    case 'synced':
    case 'failed':
      return i18n.t(`plantings:photos.syncStatus.${status}`);
  }
};

const getSyncStatusColor = (status: PhotoSyncStatus) => {
  switch (status) {
    case 'pending':
      return 'warning';
    case 'uploading':
      return 'processing';
    case 'synced':
      return 'success';
    case 'failed':
      return 'error';
  }
};

const styles = {
  grayText: css({ color: '#8c8c8c' }),
  relativePosition: css({ position: 'relative' }),
  photoCard: css({ width: 200 }),
  photoCover: css({ height: 150, objectFit: 'cover' }),
  photoTitle: css({ fontSize: 12, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }),
  errorText: css({ color: '#ff4d4f' }),
  emptyState: css({ textAlign: 'center', padding: '40px 0', color: '#8c8c8c' }),
  emptyIcon: css({ fontSize: 48, marginBottom: 16 }),
};

const PlantingPhotos: React.FC<PlantingPhotosProps> = ({
  plantingId,
  saveId,
  remotePhotos = [],
  readonly = false,
}) => {
  const { t } = useTranslation('plantings');
  const {
    localPhotos,
    isLoading,
    addPhoto,
    removeLocalPhoto,
    removeRemotePhoto,
    syncPhotos,
  } = usePlantingPhotos(saveId, plantingId);

  // Формируем полный URL для статических файлов
  const getFullUrl = (url?: string) => {
    if (!url) return '';
    
    // Если URL уже полный - возвращаем как есть
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }
    
    // Для относительных путей добавляем хост с портом 80
    const staticHost = 'http://localhost:80';
    const fullUrl = url.startsWith('/') ? `${staticHost}${url}` : `${staticHost}/${url}`;
    // console.log('🖼️ Photo URL:', url, '→', fullUrl);
    return fullUrl;
  };
  
  // Показываем только несинхронизированные локальные фото
  const unsyncedLocalPhotos = localPhotos.filter(p => p.syncStatus !== 'synced');

  const [description, setDescription] = useState('');
  const [showDescriptionModal, setShowDescriptionModal] = useState(false);
  const [pendingFile, setPendingFile] = useState<File | null>(null);

  /**
   * Обработка выбора файла
   */
  const handleBeforeUpload = (file: File) => {
    setPendingFile(file);
    setShowDescriptionModal(true);
    return false; // Предотвращаем автоматическую загрузку
  };

  /**
   * Подтверждение добавления фото с описанием
   */
  const handleConfirmUpload = async () => {
    if (!pendingFile) return;

    try {
      await addPhoto(pendingFile, description || undefined);
      setShowDescriptionModal(false);
      setDescription('');
      setPendingFile(null);
    } catch (error: any) {
      Modal.error({
        title: t('photos.errorTitle'),
        content: error?.message || t('photos.addFailed'),
      });
    }
  };

  /**
   * Удаление локального фото
   */
  const handleRemoveLocalPhoto = (photoId: string) => {
    Modal.confirm({
      title: t('photos.deleteConfirmTitle'),
      content: t('photos.deleteLocalContent'),
      okText: t('common:actions.delete'),
      cancelText: t('common:actions.cancel'),
      onOk: async () => {
        try {
          await removeLocalPhoto(photoId);
        } catch (error: any) {
          Modal.error({
            title: t('photos.errorTitle'),
            content: error?.message || t('photos.deleteFailed'),
          });
        }
      },
    });
  };

  /**
   * Удаление удалённого фото
   */
  const handleRemoveRemotePhoto = (photoId: string | number) => {
    Modal.confirm({
      title: t('photos.deleteConfirmTitle'),
      content: t('photos.deleteRemoteContent'),
      okText: t('common:actions.delete'),
      cancelText: t('common:actions.cancel'),
      onOk: async () => {
        try {
          await removeRemotePhoto(photoId);
        } catch (error: any) {
          Modal.error({
            title: t('photos.errorTitle'),
            content: error?.message || t('photos.deleteFailed'),
          });
        }
      },
    });
  };

  const totalPhotos = unsyncedLocalPhotos.length + remotePhotos.length;
  // const pendingCount = unsyncedLocalPhotos.filter((p) => p.syncStatus === 'pending' || p.syncStatus === 'failed').length;
  const failedWithMaxRetries = unsyncedLocalPhotos.filter((p) => p.syncStatus === 'failed' && p.retryCount >= 3).length;

  return (
    <div>
      <Space direction="vertical" size={16} css={commonStyles.fullWidth}>
        {/* Панель управления */}
        <Card size="small">
          <Space wrap>
            {!readonly && (
              <Upload
                beforeUpload={handleBeforeUpload}
                showUploadList={false}
                accept="image/*"
                multiple={false}
              >
                <Button type="primary" icon={<CameraOutlined />} loading={isLoading}>
                  {t('photos.add')}
                </Button>
              </Upload>
            )}

            {failedWithMaxRetries > 0 && (
              <Badge count={failedWithMaxRetries} offset={[-5, 0]}>
                <Button 
                  danger
                  icon={<CloudUploadOutlined />} 
                  onClick={syncPhotos}
                >
                  {t('photos.retryUpload')}
                </Button>
              </Badge>
            )}

            <span css={styles.grayText}>
              {t('photos.total', { count: totalPhotos })}
            </span>

            {!navigator.onLine && (
              <Tag color="warning" icon={<ExclamationCircleOutlined />}>
                {t('photos.offline')}
              </Tag>
            )}
          </Space>
        </Card>

        {/* Локальные фото (ещё не загруженные на сервер) */}
        {unsyncedLocalPhotos.length > 0 && (
          <Card
            size="small"
            title={
              <Space>
                <span>{t('photos.local')}</span>
                <Tag color="blue">{unsyncedLocalPhotos.length}</Tag>
              </Space>
            }
          >
            <Space wrap size={16}>
              {unsyncedLocalPhotos.map((photo) => (
                <div key={photo.id} css={styles.relativePosition}>
                  <Badge.Ribbon
                    text={
                      <Tooltip title={getSyncStatusText(photo.syncStatus)}>
                        {getSyncStatusIcon(photo.syncStatus)}
                      </Tooltip>
                    }
                    color={getSyncStatusColor(photo.syncStatus)}
                  >
                    <Card
                      hoverable
                      css={styles.photoCard}
                      cover={
                        <Image
                          src={photo.previewUrl}
                          alt={photo.fileName}
                          css={styles.photoCover}
                          preview={{ src: photo.previewUrl }}
                        />
                      }
                      actions={
                        !readonly
                          ? [
                              <Tooltip title={t('common:actions.delete')}>
                                <DeleteOutlined
                                  key="delete"
                                  onClick={() => handleRemoveLocalPhoto(photo.id)}
                                />
                              </Tooltip>,
                            ]
                          : []
                      }
                    >
                      <Card.Meta
                        title={
                          <div css={styles.photoTitle}>
                            {photo.fileName}
                          </div>
                        }
                        description={
                          <div css={commonStyles.tinyText}>
                            {(photo.size / 1024).toFixed(0)} KB
                            {photo.retryCount > 0 && (
                              <div css={styles.errorText}>
                                {t('photos.attempts', { count: photo.retryCount })}
                              </div>
                            )}
                          </div>
                        }
                      />
                    </Card>
                  </Badge.Ribbon>
                </div>
              ))}
            </Space>
          </Card>
        )}

        {/* Удалённые фото (с сервера) */}
        {remotePhotos.length > 0 && (
          <Card
            size="small"
            title={
              <Space>
                <CloudOutlined />
                <span>{t('photos.uploaded')}</span>
                <Tag color="green">{remotePhotos.length}</Tag>
              </Space>
            }
          >
            <Space wrap size={16}>
              {remotePhotos.map((photo) => {
                // console.log("Photo", photo);
                return (
                <Card
                  key={photo.id}
                  hoverable
                  css={styles.photoCard}
                  cover={
                    <Image
                      src={getFullUrl(photo.thumbnailUrl || photo.url)}
                      alt={photo.fileName}
                      css={styles.photoCover}
                      preview={{ src: getFullUrl(photo.url) }}
                    />
                  }
                  actions={
                    !readonly
                      ? [
                          <Tooltip title={t('common:actions.delete')}>
                            <DeleteOutlined
                              key="delete"
                              onClick={() => handleRemoveRemotePhoto(photo.id)}
                            />
                          </Tooltip>,
                        ]
                      : []
                  }
                >
                  <Card.Meta
                    title={
                      <div css={styles.photoTitle}>
                        {photo.fileName}
                      </div>
                    }
                    description={
                      <div css={commonStyles.tinyText}>
                        {photo.description || (photo.size / 1024).toFixed(0) + ' KB'}
                      </div>
                    }
                  />
                </Card>
                );
              })}
            </Space>
          </Card>
        )}

        {totalPhotos === 0 && (
          <Card>
            <div css={styles.emptyState}>
              <CameraOutlined css={styles.emptyIcon} />
              <div>{t('photos.empty')}</div>
            </div>
          </Card>
        )}
      </Space>

      {/* Модальное окно для добавления описания */}
      <Modal
        title={t('photos.addDescription')}
        open={showDescriptionModal}
        onOk={handleConfirmUpload}
        onCancel={() => {
          setShowDescriptionModal(false);
          setDescription('');
          setPendingFile(null);
        }}
        okText={t('common:actions.add')}
        cancelText={t('common:actions.cancel')}
        confirmLoading={isLoading}
      >
        <Space direction="vertical" css={commonStyles.fullWidth}>
          {pendingFile && (
            <div>
              <strong>{t('photos.file')}</strong> {pendingFile.name} ({(pendingFile.size / 1024).toFixed(0)} KB)
            </div>
          )}
          <TextArea
            placeholder={t('photos.descriptionPlaceholder')}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            maxLength={500}
          />
        </Space>
      </Modal>
    </div>
  );
};

export default PlantingPhotos;
